import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Current app version (must match version.json on server for no update notification)
export const APP_VERSION = "0.3.9";

export default function UpdateNotification() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await axios.get(`${API}/api/version`);
        const latestVersion = response.data.version;
        setServerVersion(latestVersion);
        
        // Compare versions
        if (latestVersion && compareVersions(latestVersion, APP_VERSION) > 0) {
          // Check if user already dismissed this version
          const dismissedVersion = localStorage.getItem("dismissed_version");
          if (dismissedVersion !== latestVersion) {
            setUpdateAvailable(true);
          }
        }
      } catch (error) {
        console.error("Failed to check version:", error);
      }
    };

    checkVersion();
    // Check every 5 minutes
    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const compareVersions = (v1, v2) => {
    const parts1 = v1.split(".").map(Number);
    const parts2 = v2.split(".").map(Number);
    
    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const p1 = parts1[i] || 0;
      const p2 = parts2[i] || 0;
      if (p1 > p2) return 1;
      if (p1 < p2) return -1;
    }
    return 0;
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (serverVersion) {
      localStorage.setItem("dismissed_version", serverVersion);
    }
  };

  const handleUpdate = () => {
    // For web app, just reload the page
    // For mobile app (Capacitor), this would trigger app store redirect
    window.location.reload(true);
  };

  if (!updateAvailable || dismissed) {
    return null;
  }

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] max-w-md w-full mx-4">
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-2xl rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
            <Download className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">
              🎉 Aggiornamento disponibile!
            </p>
            <p className="text-xs opacity-90 mt-0.5">
              Nuova versione {serverVersion} disponibile (attuale: {APP_VERSION})
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                onClick={handleUpdate}
                className="gap-1 bg-white text-emerald-600 hover:bg-emerald-50 text-xs h-8 px-3"
              >
                <Download className="h-3 w-3" />
                Aggiorna ora
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="text-white hover:bg-white/20 text-xs h-8 px-3"
              >
                Più tardi
              </Button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="text-white hover:bg-white/20 h-6 w-6 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Version display component for footer
export function VersionBadge({ className = "" }) {
  return (
    <span className={`text-xs text-slate-500 ${className}`}>
      v{APP_VERSION}
    </span>
  );
}
